/**
 * Shared "out of credits" modal — shown anywhere the credit gate trips.
 *
 * Used by:
 * - TopNav's "New Analysis" tab (when balance=0)
 * - Dashboard's "New Analysis" button + empty-state CTA
 *
 * Before this component existed, the modal was inlined in Dashboard.tsx and
 * TopNav tripped the gate but never rendered a modal — clicks silently failed.
 */

import { Link } from 'react-router-dom'
import { X, Sparkles } from 'lucide-react'

interface OutOfCreditsModalProps {
  open: boolean
  onClose: () => void
}

export default function OutOfCreditsModal({ open, onClose }: OutOfCreditsModalProps) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-8 border border-gray-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0D3B66]/10 rounded-full mb-6">
            <Sparkles className="w-8 h-8 text-[#0D3B66]" />
          </div>
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">
            You're out of credits!
          </h3>
          <p className="text-gray-600 mb-2">
            But don't despair — you've already seen what FitToHire can do.
          </p>
          <p className="text-gray-600 mb-6">
            Pick up more credits starting at{' '}
            <span className="font-semibold text-gray-900">$8.99</span> per report,
            grab a <span className="font-semibold text-gray-900">5-pack for $24.99</span>,
            or go unlimited with Pro at{' '}
            <span className="font-semibold text-gray-900">$29.99/mo</span>.
          </p>
          <Link
            to="/pricing"
            onClick={onClose}
            className="block w-full py-3 px-6 bg-[#0D3B66] hover:bg-[#0a2d4d] text-white font-semibold rounded-lg transition-all shadow-lg shadow-[#0D3B66]/20 text-center"
          >
            See pricing options
          </Link>
        </div>
      </div>
    </div>
  )
}
